/**
 * The cockpit's tab bars — one compact style, used by the outer tabs and by every sub-tab.
 *
 * The design-system trigger is built for a page-level nav: `h-9` rising to `sm:h-12 md:min-h-14`
 * with `md:body-16-semibold`, which is a ~56px bar of 16px semibold text. The cockpit stacks up to
 * two levels of it above the figures, and that pushed the numbers down the page for chrome nobody
 * reads twice. This is a pill bar instead: 32px tall, 13px medium, fully rounded.
 *
 * The `!` prefixes are not decoration — the atom sets those heights and paddings at three
 * breakpoints, so plain utilities lose to it on width. `views/taActionBoard/BoardChrome.tsx` uses
 * the same escape for the same reason.
 */
import { Tabs, TabsList, TabsTrigger } from '@finn-auto/design-system/atoms/tabs';
import { cn } from '../../../../shared';

const LIST_BASE = 'inline-flex w-auto items-center gap-0.5 bg-cotton !p-1 border-0 [&>button]:!min-h-0';

const TRIGGER_BASE =
  '!h-8 !min-h-8 sm:!h-8 md:!min-h-8 !px-3.5 md:!px-3.5 ' +
  '!text-[13px] !font-medium !leading-none text-iron ' +
  'data-[state=active]:!bg-white data-[state=active]:text-black ' +
  'data-[state=active]:shadow-[0_1px_2px_rgba(26,26,26,0.10)] ' +
  'hover:text-black';

/**
 * `pill` is the cockpit's own segmented control and stays the default — every existing bar keeps
 * it. `soft` exists for pages that put the tab bar in a row of other chrome (filters, buttons, a
 * banner, a table) and want one radius across all of it: a fully-rounded bar is the one shape that
 * can't match a 6px box, so it gets the same `rounded-md` as its neighbours.
 */
const SHAPES = {
  pill: { list: 'rounded-full', trigger: '!rounded-full' },
  soft: { list: 'rounded-md', trigger: '!rounded-md' },
} as const;

export type CockpitTabShape = keyof typeof SHAPES;

/**
 * `sub` is for a bar that sits UNDER another one (Company View / Cluster View and the like): 28px
 * and 12px, so the second level reads as second and costs less height (Theresa, Sep 29 2026).
 */
const SIZES = {
  // `body-14-semibold` lives here rather than in TRIGGER_BASE so `sub` never has to out-rank it.
  default: { list: '', trigger: 'md:!body-14-semibold' },
  sub: { list: '!p-0.5', trigger: '!h-7 !min-h-7 sm:!h-7 md:!min-h-7 !px-3 md:!px-3 !text-xs' },
} as const;

export type CockpitTabSize = keyof typeof SIZES;

export interface CockpitTab<T extends string> {
  value: T;
  label: string;
}

export function CockpitTabs<T extends string>({
  value,
  tabs,
  onChange,
  className,
  ariaLabel,
  shape = 'pill',
  size = 'default',
}: {
  value: T;
  tabs: readonly CockpitTab<T>[];
  onChange: (next: T) => void;
  className?: string;
  ariaLabel?: string;
  shape?: CockpitTabShape;
  size?: CockpitTabSize;
}) {
  const { list, trigger } = SHAPES[shape];
  const sized = SIZES[size];
  return (
    <Tabs value={value} onValueChange={(v) => onChange(v as T)} className={className}>
      <TabsList className={cn(LIST_BASE, list, sized.list)} aria-label={ariaLabel}>
        {tabs.map((t) => (
          <TabsTrigger key={t.value} value={t.value} className={cn(TRIGGER_BASE, trigger, sized.trigger)}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/** For a bar that sits directly under a heading, where the default margin is too much. */
export function subTabClass(extra?: string): string {
  return cn('mb-1', extra);
}
